import {
    AnyCaptcha,
    AutowireParameters,
    OnSolverCallback,
} from '../../../any-captcha/src/frontend/AnyCaptcha';
import defaultAutowireParameters from "../../../any-captcha/src/frontend/defaultAutowireParameters";

/**
 * @see https://yandex.cloud/ru/docs/smartcaptcha/concepts/widget-methods#render
 */
export interface RenderParameters {
    /**
     * Ключ клиентской части.
     *
     * @see https://yandex.cloud/ru/docs/smartcaptcha/concepts/keys
     */
    sitekey: string,

    /**
     * Функция-обработчик получения токена результата.
     */
    callback?: (token: string) => void,

    /**
     * Язык виджета.
     */
    hl?: 'ru' | 'en' | 'be' | 'kk' | 'tt' | 'uk' | 'uz' | 'tr',

    /**
     * Включение работы капчи в режиме тестирования.
     * Пользователь всегда будет получать задание.
     * Используйте это свойство только для отладки и тестирования.
     */
    test?: boolean,

    /**
     * Запуск капчи в WebView.
     *
     * Используется для повышения точности оценки пользователей при добавлении
     * капчи в мобильные приложения с помощью WebView.
     */
    webview?: boolean,

    /**
     * Невидимая капча.
     *
     * @see https://yandex.cloud/ru/docs/smartcaptcha/concepts/invisible-captcha
     */
    invisible?: boolean,

    /**
     * Расположение блока с уведомлением об обработке данных.
     *
     * @see https://yandex.cloud/ru/docs/smartcaptcha/concepts/invisible-captcha#data-processing-notice
     */
    shieldPosition?: 'top-left' | 'center-left' | 'bottom-left' | 'top-right' | 'center-right' | 'bottom-right',

    /**
     * Скрыть блок с уведомлением об обработке данных.
     *
     * @see https://yandex.cloud/ru/docs/smartcaptcha/concepts/invisible-captcha#data-processing-notice
     */
    hideShield?: boolean,
}

export type WidgetId = string;

export type SubscribeEvent =
    | 'challenge-visible'
    | 'challenge-hidden'
    | 'network-error'
    | 'javascript-error'
    | 'success'
    | 'token-expired';

export interface YandexSmartCaptchaProvider {
    render(container: HTMLElement | string, params: RenderParameters): WidgetId;
    getResponse(widgetId: WidgetId | undefined): string;
    execute(widgetId: WidgetId | undefined): void;
    reset(widgetId: WidgetId | undefined): void;
    destroy(widgetId: WidgetId | undefined): void;
    subscribe(widgetId: WidgetId, event: SubscribeEvent, callback: Function): Function;
}

const onloadFunctionName = 'onYandexSmartCaptchaLoaded';

declare global {
    interface Window {
        smartCaptcha: YandexSmartCaptchaProvider | null;
        [onloadFunctionName]: (() => void) | null;
    }
}

export class YandexSmartCaptcha implements AnyCaptcha {
    private provider: YandexSmartCaptchaProvider|null = null;
    private readonly parameters: RenderParameters;

    constructor(parameters: RenderParameters) {
        this.parameters = parameters;

        window[onloadFunctionName] = (): void => {
            this.provider = window.smartCaptcha;
        }

        const script = document.createElement('script');
        script.src = 'https://smartcaptcha.yandexcloud.net/captcha.js?render=onload&onload=' + onloadFunctionName;
        script.defer = true;
        document.body.append(script);
    }

    async renderChallenge(container: HTMLElement, onSolved: OnSolverCallback): Promise<WidgetId> {
        const callback = (token) => {
            onSolved(token);
            this.parameters.callback?.call(null, token);
        };

        return (await this.getProvider()).render(container, Object.assign({callback}, this.parameters));
    }

    public getProvider(): Promise<YandexSmartCaptchaProvider> {
        if (this.provider) {
            return Promise.resolve(this.provider);
        }

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if (this.provider === null) {
                    return;
                }

                clearInterval(interval);
                resolve(this.provider);
            });
        });
    }

    async autowire(parameters: Partial<AutowireParameters> = {}): Promise<void> {
        const resolvedParameters = Object.assign({}, defaultAutowireParameters, parameters);
        const provider = await this.getProvider();
        const containers = document.querySelectorAll<HTMLElement>(resolvedParameters.challengeContainerSelector);

        await Promise.all(
            Array.from(containers).map(
                (container) => this.autowireContainer(provider, container, resolvedParameters)
            )
        );
    }

    private async autowireContainer(provider: YandexSmartCaptchaProvider, container: HTMLElement, parameters: Partial<AutowireParameters> = {}): Promise<void> {
        const form: HTMLFormElement = container.closest(parameters.formContainerSelector);
        if (!(form instanceof HTMLFormElement)) {
            throw new Error(`Unable to mount submit handler into ${parameters.formContainerSelector} form. Do it manually.`);
        }

        if ('yandexSmartCaptchaInited' in form.dataset) {
            return;
        }

        // TODO crutch to allow multiple autowire calls without side-effects.
        //      Other architectural decisions are needed.
        form.dataset.yandexSmartCaptchaInited = '1';

        let widgetId: string | undefined = undefined;

        const onSubmitted = (event: SubmitEvent): boolean => {
            event.preventDefault();
            // if (!widgetId) {
            //     throw new Error('Form were submitted before challenge were rendered.');
            // }

            provider.execute(widgetId); // would call onSolved later

            return false;
        };

        const registerSubmitListener = () => form.addEventListener('submit', onSubmitted);
        const unregisterSubmitListener = () => form.removeEventListener('submit', onSubmitted);

        const onSolved = (solution) => {
            const solutionInput = this.ensureSolutionInput(
                form,
                parameters.solitionInputName,
                parameters.autocreateSolutionInput
            );

            solutionInput.value = solution;

            unregisterSubmitListener();

            switch (parameters.onSolvedAction) {
                case 'sendSubmitEvent':
                    form.dispatchEvent(new SubmitEvent('submit'));
                    break;
                case 'submit':
                    form.submit();
                    break;
                case 'none':
                    break;
                default:
                    if (typeof parameters.onSolvedAction === 'function') {
                        parameters.onSolvedAction.call(form);
                        break;
                    }

                    throw new Error(`Unknown onSolvedAction (${parameters.onSolvedAction}).`);
            }

            registerSubmitListener();
        };

        widgetId = await this.renderChallenge(container, onSolved);
        registerSubmitListener();
    }

    private ensureSolutionInput(container: Element, name: string, autocreate: boolean): HTMLInputElement {
        const inputs: Array<HTMLInputElement> = Array.from(container.querySelectorAll(`input[name="${name}"]`));
        if (inputs.length > 1) {
            throw new Error(`Unable to find unique solution input for name "${name}". Found ${inputs.length} of them.`);
        }

        if (inputs.length === 0) {
            if (autocreate === false) {
                throw new Error(`Unalbe to find input ${name} and its autocreating is forbidden. You should create the input manually or enable autocreateSolutionInput parameter.`);
            }

            const input = document.createElement('input');
            input.name = name;
            input.type = 'hidden';

            inputs.push(input);
        }

        return inputs[0];
    }
}
